'use client'

import { IconMinus, IconPlus } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import { pathOr } from 'ramda'

import { CartItem } from 'modules/cart'
import { getProductUnitDetails } from 'modules/products'
import { ButtonSizes } from 'shared/ui/Button/Button.enums'
import Checkbox, { CheckboxProps } from 'shared/ui/form/Checkbox/Checkbox'
import IconButton from 'shared/ui/IconButton'

import styles from './ProfileSaleOrderSelectableProductCard.module.scss'

interface ProfileSaleOrderSelectableProductCardProps {
  item: CartItem
  selectedCount?: number
  maxSelectableCount: number
  isSubmitting: boolean
  onToggle: (item: CartItem, checked: boolean) => void
  onChangeCount: (item: CartItem, delta: number) => void
}

const ProfileSaleOrderSelectableProductCard = ({
  item,
  selectedCount,
  maxSelectableCount,
  isSubmitting,
  onToggle,
  onChangeCount
}: ProfileSaleOrderSelectableProductCardProps) => {
  const productImage = pathOr(
    '/static/photo-placeholder.svg',
    ['files', 0, 'thumbnailPathSmall'],
    item.product
  )

  const isItemBlockedForReturn = item.isReturnable === false
  const isDisabled = isItemBlockedForReturn || isSubmitting
  const unitDetails = getProductUnitDetails(item.product)

  return (
    <div className={styles.productBlock}>
      {isItemBlockedForReturn && (
        <p className={styles.notReturnableLabel}>Tego produktu nie można zwrócić</p>
      )}

      <div
        className={clsx(styles.productRow, {
          [styles.productRowDisabled]: isItemBlockedForReturn
        })}
      >
        <div className={styles.selectColumn}>
          <ItemCheckbox
            id={`profile_order_return_item_${item.id}`}
            name={item.id}
            label=''
            aria-label={`Wybierz do zwrotu: ${item.product.name}`}
            value={Boolean(selectedCount)}
            disabled={isDisabled}
            noMargin
            onChange={(_, checked) => onToggle(item, checked)}
          />
        </div>

        <div className={styles.productImageWrapper}>
          <Image
            src={productImage}
            alt={item.product.name}
            width={78}
            height={78}
          />
        </div>

        <div className={styles.productDetails}>
          <p className={styles.brandName}>
            {item.product.brand?.name || item.product.nameShort}
          </p>
          <p className={styles.productName}>{item.product.name}</p>
          <p className={styles.productMeta}>{unitDetails}</p>
        </div>

        <div className={styles.quantityControls}>
          <IconButton
            className={styles.quantityIconButton}
            onClick={() => onChangeCount(item, -1)}
            disabled={isDisabled || !selectedCount}
            icon={<IconMinus />}
            size={ButtonSizes.small}
            variant='green'
            aria-label='Zmniejsz liczbę'
          />
          <span className={styles.quantityValue}>
            {selectedCount ? `${selectedCount} szt` : '0 szt'}
          </span>

          <IconButton
            className={styles.quantityIconButton}
            onClick={() => onChangeCount(item, 1)}
            disabled={isDisabled || (selectedCount || 0) >= maxSelectableCount}
            icon={<IconPlus />}
            size={ButtonSizes.small}
            variant='green'
            aria-label='Zwiększ liczbę'
          />
        </div>
        <p
          className={styles.priceColumn}
          dangerouslySetInnerHTML={{ __html: item.valueGross.formatted }}
        />
      </div>
    </div>
  )
}

export const ItemCheckbox = ({ className, ...props }: CheckboxProps) => (
  <Checkbox {...props} className={clsx(styles.itemCheckbox, className)} />
)

export default ProfileSaleOrderSelectableProductCard
